import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import StatusMessage from '../components/ui/StatusMessage';
import {
  getDashboardSummary,
  getRecentProducts,
  getRecentSuppliers,
} from '../services/firestoreService';

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const summaryCardStyle = {
  backgroundColor: '#ffffff',
  borderRadius: 12,
  border: '1px solid #e5e7eb',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const summaryCardFooterStyle = {
  marginTop: 'auto',
  paddingTop: 4,
};

const summaryLabelStyle = {
  margin: 0,
  color: '#64748b',
  fontSize: 14,
  fontWeight: 600,
};

const summaryValueStyle = {
  margin: 0,
  color: '#0f172a',
  fontSize: 32,
  fontWeight: 700,
  lineHeight: 1.1,
};

const sectionCardStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const sectionTitleRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

const sectionTitleStyle = {
  margin: 0,
  color: '#0f172a',
  fontSize: 18,
  fontWeight: 700,
};

const sectionCaptionStyle = {
  margin: 0,
  color: '#64748b',
  fontSize: 13,
};

const tableStyle = {
  width: '100%',
  cursor: 'default',
};

const tableRowStyle = {
  cursor: 'default',
};

const tableHeadCellStyle = {};

const tableCellStyle = {};

const primaryCellStyle = {
  ...tableCellStyle,
  color: '#0f172a',
  fontWeight: 600,
};

const loadingTextStyle = {
  margin: 0,
  color: '#64748b',
  fontSize: 14,
};

const emptyStateStyle = {
  margin: 0,
  padding: '12px 0 2px',
  color: '#64748b',
  fontSize: 14,
};

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatCreatedAt = (value) => {
  const parsedDate = normalizeDate(value);

  if (!parsedDate) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsedDate);
};

function SummaryActionButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn btn-secondary"
      style={{ fontSize: 12, padding: '8px 12px' }}
    >
      {label}
    </button>
  );
}

function SummaryCard({ label, value, isLoading, actionLabel, onActionClick }) {
  return (
    <article className="metric-card" style={summaryCardStyle}>
      <p style={summaryLabelStyle}>{label}</p>
      <p className="metric-number" style={summaryValueStyle}>{isLoading ? '--' : value}</p>
      <div style={summaryCardFooterStyle}>
        <SummaryActionButton label={actionLabel} onClick={onActionClick} />
      </div>
    </article>
  );
}

function ActivityNameButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="clickable-name"
    >
      {label}
    </button>
  );
}

function ActivityTable({
  title,
  caption,
  items,
  nameKey,
  emptyMessage,
  onNameClick,
}) {
  return (
    <section className="section" style={sectionCardStyle}>
      <div className="section-header" style={sectionTitleRowStyle}>
        <h2 className="section-title" style={sectionTitleStyle}>{title}</h2>
        <span style={sectionCaptionStyle}>{caption}</span>
      </div>

      {items.length === 0 ? (
        <p style={emptyStateStyle}>{emptyMessage}</p>
      ) : (
        <div className="table-wrapper">
          <table className="data-table" style={tableStyle}>
          <thead>
            <tr style={tableRowStyle}>
              <th style={tableHeadCellStyle}>Name</th>
              <th style={tableHeadCellStyle}>Created At</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} style={tableRowStyle}>
                <td style={primaryCellStyle}>
                  <ActivityNameButton
                    label={item[nameKey] || '-'}
                    onClick={() => onNameClick(item.id)}
                  />
                </td>
                <td style={tableCellStyle}>{formatCreatedAt(item.createdAt)}</td>
              </tr>
            ))}
          </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [summary, setSummary] = useState({
    suppliers: 0,
    products: 0,
    categories: 0,
  });
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentSuppliers, setRecentSuppliers] = useState([]);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      setPageError('');

      try {
        const [summaryData, productData, supplierData] = await Promise.all([
          getDashboardSummary(),
          getRecentProducts(5),
          getRecentSuppliers(5),
        ]);

        setSummary(summaryData);
        setRecentProducts(Array.isArray(productData) ? productData : []);
        setRecentSuppliers(Array.isArray(supplierData) ? supplierData : []);
      } catch (error) {
        console.error('Error loading dashboard:', error);
        setSummary({
          suppliers: 0,
          products: 0,
          categories: 0,
        });
        setRecentProducts([]);
        setRecentSuppliers([]);
        setPageError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  return (
    <div className="page dashboard-page">
      <header className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">
            Monitor key totals and review the latest actions across suppliers and
            products.
          </p>
        </div>
      </header>

      <StatusMessage tone="error" message={pageError} />

      <section className="section">
        <div className="section-header">
          <h2>Metrics</h2>
          <span>Current totals</span>
        </div>

        <div className="metrics-grid" style={summaryGridStyle}>
          <SummaryCard
            label="Total Suppliers"
            value={summary.suppliers}
            isLoading={loading}
            actionLabel="View All Suppliers"
            onActionClick={() => navigate('/suppliers')}
          />
          <SummaryCard
            label="Total Products"
            value={summary.products}
            isLoading={loading}
            actionLabel="View All Products"
            onActionClick={() => navigate('/products')}
          />
          <SummaryCard
            label="Total Categories"
            value={summary.categories}
            isLoading={loading}
            actionLabel="View All Categories"
            onActionClick={() => navigate('/categories')}
          />
        </div>
      </section>

      <ActivityTable
        title="Latest Products"
        caption="Last 5 created"
        items={recentProducts}
        nameKey="name"
        onNameClick={(id) => navigate(`/products/${id}`)}
        emptyMessage={
          loading ? 'Loading recent products...' : 'No recent products found.'
        }
      />
      <ActivityTable
        title="Latest Suppliers"
        caption="Last 5 created"
        items={recentSuppliers}
        nameKey="companyName"
        onNameClick={(id) => navigate(`/suppliers/${id}`)}
        emptyMessage={
          loading
            ? 'Loading recent suppliers...'
            : 'No recent suppliers found.'
        }
      />

      {loading ? <p style={loadingTextStyle}>Loading dashboard data...</p> : null}
    </div>
  );
}
